import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  HostBinding,
} from '@angular/core';

import { icons } from '@finnairoyj/fcom-ui-styles';

import { isNotBlank } from '@fcom/core/utils/utils';

export enum NotificationTheme {
  TRANSPARENT = 'transparent',
  DISRUPTION_LEVEL_FOUR = 'disruption_level_4',
  DISRUPTION_LEVEL_THREE = 'disruption_level_3',
  DISRUPTION_LEVEL_TWO = 'disruption_level_2',
  DISRUPTION_LEVEL_ONE = 'disruption_level_1',
  ALERT = 'alert',
  WARNING = 'warning',
  INFO = 'info',
  SUCCESS = 'success',
  SUSTAINABILITY = 'sustainability',
}

export enum NotificationLayout {
  BLOCK = 'block',
  BLOCK_MAX_GRID = 'block_max_grid',
  INLINE = 'inline',
}

export enum ContentSpaceClass {
  NONE = '',
  PS_XSMALL = 'ps-xsmall',
  PS_XSMALL_Y = 'ps-xsmall-y',
}

export enum BorderDirection {
  ROUNDED_TOP_LARGE = 'rounded-top-large',
  ROUNDED_BOTTOM_LARGE = 'rounded-bottom-large',
  ROUNDED_MEDIUM = 'rounded-medium',
}

export interface NotificationI18n {
  closeAriaLabel: string;
}

/**
 * A component to display varied forms of notifications.
 *
 * Zeplin designs are available [here](https://zpl.io/adG00Wl)
 *
 * There is many options available, but in many cases you won't need most of these.
 *
 * @example
 * <fcom-notification
 *   [theme]="theme"
 *   [layout]="layout"
 *   [isClosable]="isClosable"
 *   [hasDropShadow]="hasDropShadow"
 *   [linkText]="linkText"
 *   [linkUrl]="linkUrl"
 *   [iconName]="iconName"
 *   [contentSpaceClass]="ContentSpaceClass.PX_SMALL_Y"
 *   [borderDirection]="BorderDirection.ROUNDED_BOTTOM_LARGE"
 *   (linkClick)="linkClick($event)"
 *   (closeClick)="closeClick($event)">
 *   {{text}}
 * </fcom-notification>
 */
@Component({
  selector: 'fcom-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationComponent implements OnInit, OnChanges {
  public readonly NotificationTheme = NotificationTheme;
  public readonly NotificationLayout = NotificationLayout;

  @HostBinding('class.inline-block') get inlineBlockClass(): boolean {
    return this.layout === NotificationLayout.INLINE;
  }
  @HostBinding('class.block') get blockClass(): boolean {
    return this.layout !== NotificationLayout.INLINE;
  }

  /**
   * What color scheme to use for the component
   */
  @Input()
  theme: NotificationTheme = NotificationTheme.INFO;

  /**
   * Whether the component should be inline, block or full width with max-grid
   */
  @Input()
  layout: NotificationLayout = NotificationLayout.BLOCK;

  /**
   * Whether to show the close icon or not
   */
  @Input()
  isClosable = false;

  /**
   * Whether to display a drop shadow around the notification
   */
  @Input()
  hasDropShadow = false;

  /**
   * The text to use for a link. If no URL is passed it will be rendered as a button. Otherwise it
   * will be an a tag.
   */
  @Input()
  linkText: string;

  /**
   * Whether to display link text wrapped (on the same line) or not (on a new line).
   */
  @Input()
  linkTextWrapped = true;

  /**
   * The url to go to. If you wish to handle this behavior yourself you can listen to the linkClick
   * event and `event.preventDefault` to stop the browser changing the page.
   */
  @Input()
  linkUrl: string;

  /**
   * Whether the link should open in a new window or not. Only applicable when linkText + linkUrl
   * is provided.
   */
  @Input()
  linkOpenInNewWindow = false;

  /**
   * An icon to override the default icon used. Note that each NotificationTheme has a different
   * default icon
   */
  @Input()
  iconName: string;

  @Input()
  iconCategory: keyof icons = 'svg-library';

  /**
   * Any i18n labels used by the component. In most cases you can use the default for the component
   */
  @Input()
  i18n: NotificationI18n = {
    closeAriaLabel: 'actions.close',
  };

  /**
   * Any class for managing spacing for notification content.
   */
  @Input()
  contentSpaceClass?: ContentSpaceClass = ContentSpaceClass.NONE;

  /**
   * Any class for managing border radius direction.
   */
  @Input()
  borderDirection?: BorderDirection = BorderDirection.ROUNDED_MEDIUM;

  /**
   * When the button/a tag is clicked the parent component can listen and handle the event. Note
   * that the event is sent so that you can stopPropagation/preventDefault if needed.
   */
  @Output()
  linkClick = new EventEmitter<Event>();

  /**
   * The close button was clicked and the notification should be closed. The parent component
   * should either remove the notification from the dom or hide it in some way.
   */
  @Output()
  closeClick = new EventEmitter<Event>();

  displayedIcon: string;

  ngOnInit(): void {
    this.displayedIcon = this.iconName ?? this.getDefaultIconForTheme();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.theme && !this.iconName) || (changes.iconName && !changes.iconName.currentValue)) {
      this.displayedIcon = this.getDefaultIconForTheme();
    }

    if (changes.iconName && changes.iconName.currentValue) {
      this.displayedIcon = changes.iconName.currentValue;
    }
  }

  get notificationStyle(): Record<string, boolean> {
    return {
      'br-green': [NotificationTheme.DISRUPTION_LEVEL_ONE, NotificationTheme.SUCCESS].includes(this.theme),
      'br-blue': [NotificationTheme.DISRUPTION_LEVEL_TWO, NotificationTheme.INFO].includes(this.theme),
      'br-yellow': [NotificationTheme.DISRUPTION_LEVEL_THREE, NotificationTheme.WARNING].includes(this.theme),
      'br-red': [NotificationTheme.ALERT, NotificationTheme.DISRUPTION_LEVEL_FOUR].includes(this.theme),
      'br-sustainability': this.theme === NotificationTheme.SUSTAINABILITY,
      'inline-block': this.layout === NotificationLayout.INLINE,
      block: this.layout !== NotificationLayout.INLINE,
      shadow: this.hasDropShadow,
      [this.borderDirection]: true,
    };
  }

  get contentStyle(): { [id: string]: boolean } {
    const paddingClass = `${this.contentSpaceClass}`;

    const styleObject: { [id: string]: boolean } = {};
    if (isNotBlank(paddingClass)) {
      styleObject[paddingClass] = true;
    } else {
      styleObject['content-container'] = true;
      styleObject[ContentSpaceClass.PS_XSMALL_Y] = this.theme !== NotificationTheme.TRANSPARENT;
    }

    return styleObject;
  }

  private getDefaultIconForTheme(): 'alert-block' | 'info-block' {
    if (this.theme === NotificationTheme.DISRUPTION_LEVEL_FOUR || this.theme === NotificationTheme.ALERT) {
      return 'alert-block';
    }

    return 'info-block';
  }
}
